import {tickIncrement} from 'd3-array';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Steps from './Steps';
import Marks from './Marks';
import Handle from '../Handle';
import * as utils from '../utils';
import { getPercent, getPercentFromValue, isNumber, isString } from '@cutwise/utils';

function noop() {}

export default function createSlider(Component) {
  return class ComponentEnhancer extends Component {
    static displayName = `ComponentEnhancer(${Component.displayName})`;

    static propTypes = {
      ...Component.propTypes,
      min: PropTypes.number,
      max: PropTypes.number,
      step: PropTypes.number,
      marks: PropTypes.object,
      marksStyles: PropTypes.object,
      included: PropTypes.bool,
      className: PropTypes.string,
      prefixCls: PropTypes.string,
      disabled: PropTypes.bool,
      children: PropTypes.node,
      onBeforeChange: PropTypes.func,
      onChange: PropTypes.func,
      onAfterChange: PropTypes.func,
      handle: PropTypes.func,
      dots: PropTypes.bool,
      vertical: PropTypes.bool,
      style: PropTypes.object,
      minimumTrackStyle: PropTypes.object, // just for compatibility, will be deperecate
      maximumTrackStyle: PropTypes.object, // just for compatibility, will be deperecate
      handleStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
      trackStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
      railStyle: PropTypes.object,
      dotStyle: PropTypes.object,
      activeDotStyle: PropTypes.object,
      autoFocus: PropTypes.bool,
      onFocus: PropTypes.func,
      onBlur: PropTypes.func,
    };

    static defaultProps = {
      ...Component.defaultProps,
      prefixCls: 'rc-slider',
      className: '',
      min: 0,
      max: 100,
      step: 1,
      marks: {},
      marksStyles: null,
      handle({ index, ...restProps }) {
        delete restProps.dragging;
        return <Handle {...restProps} key={index} />;
      },
      onBeforeChange: noop,
      onChange: noop,
      onAfterChange: noop,
      included: true,
      disabled: false,
      dots: false,
      vertical: false,
      trackStyle: [{}],
      handleStyle: [{}],
      railStyle: {},
      dotStyle: {},
      activeDotStyle: {},
    };

    constructor(props) {
      super(props);

      this.handlesRefs = {};
    }

    componentWillUnmount() {
      if (super.componentWillUnmount) super.componentWillUnmount();
      this.removeDocumentEvents();
    }

    onMouseDown = (e) => {
      if (e.button !== 0) {
        return;
      }

      const isVertical = this.props.vertical;
      let position = utils.getMousePosition(isVertical, e);
      if (!utils.isEventFromHandle(e, this.handlesRefs)) {
        this.dragOffset = 0;
      } else {
        const handlePosition = utils.getHandleCenterPosition(isVertical, e.target);
        this.dragOffset = position - handlePosition;
        position = handlePosition;
      }
      this.removeDocumentEvents();
      this.onStart(position);
      this.addDocumentMouseEvents();
    };

    onTouchStart = (e) => {
      if (utils.isNotTouchEvent(e)) return;

      const isVertical = this.props.vertical;
      let position = utils.getTouchPosition(isVertical, e);
      if (!utils.isEventFromHandle(e, this.handlesRefs)) {
        this.dragOffset = 0;
      } else {
        const handlePosition = utils.getHandleCenterPosition(isVertical, e.target);
        this.dragOffset = position - handlePosition;
        position = handlePosition;
      }
      this.onStart(position);
      this.addDocumentTouchEvents();
      utils.pauseEvent(e);
    };

    onFocus = (e) => {
      const { onFocus, vertical } = this.props;
      if (utils.isEventFromHandle(e, this.handlesRefs)) {
        const handlePosition = utils.getHandleCenterPosition(vertical, e.target);
        this.dragOffset = 0;
        this.onStart(handlePosition);
        utils.pauseEvent(e);
        if (onFocus) {
          onFocus(e);
        }
      }
    };

    onBlur = (e) => {
      const { onBlur } = this.props;
      this.onEnd(e);
      if (onBlur) {
        onBlur(e);
      }
    };

    onMouseUp = () => {
      if (this.handlesRefs[this.prevMovedHandleIndex]) {
        this.handlesRefs[this.prevMovedHandleIndex].clickFocus();
      }
    };

    onMouseMove = (e) => {
      if (!this.sliderRef) {
        this.onEnd();
        return;
      }
      const position = utils.getMousePosition(this.props.vertical, e);
      this.onMove(e, position - this.dragOffset);
    };

    onTouchMove = (e) => {
      if (utils.isNotTouchEvent(e) || !this.sliderRef) {
        this.onEnd();
        return;
      }

      const position = utils.getTouchPosition(this.props.vertical, e);
      this.onMove(e, position - this.dragOffset);
    };

    onKeyDown = (e) => {
      if (this.sliderRef && utils.isEventFromHandle(e, this.handlesRefs)) {
        this.onKeyboard(e);
      }
    };

    onClickMarkLabel = (e, value) => {
      e.stopPropagation();
      this.onChange({ value });
    };

    getSliderStart() {
      const slider = this.sliderRef;
      const rect = slider.getBoundingClientRect();

      return this.props.vertical ? rect.top : rect.left;
    }

    getSliderLength() {
      const slider = this.sliderRef;
      if (!slider) {
        return 0;
      }

      const coords = slider.getBoundingClientRect();
      return this.props.vertical ? coords.height : coords.width;
    }

    addDocumentTouchEvents() {
      // just work for Chrome iOS Safari and Android Browser
      document.addEventListener('touchmove', this.onTouchMove);
      document.addEventListener('touchend', this.onEnd);
    }

    addDocumentMouseEvents() {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onEnd);
    }

    removeDocumentEvents() {
      /* eslint-disable no-unused-expressions */
      document.removeEventListener('touchmove', this.onTouchMove);
      document.removeEventListener('touchend', this.onEnd);
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onEnd);
      /* eslint-enable no-unused-expressions */
    }

    focus() {
      if (!this.props.disabled) {
        this.handlesRefs[0].focus();
      }
    }

    blur() {
      if (!this.props.disabled) {
        Object.keys(this.handlesRefs).forEach((key) => {
          if (this.handlesRefs[key] && this.handlesRefs[key].blur) {
            this.handlesRefs[key].blur();
          }
        });
      }
    }

    calcValue(offset) {
      const { vertical, min, max } = this.props;
      const ratio = Math.abs(Math.max(offset, 0) / this.getSliderLength());
      const percent = vertical ? (1 - ratio) * 100 : ratio * 100;
      const customScale = this.getCustomScale();

      if (customScale) {
        return this.getValueByCustomOffset(percent, customScale);
      }

      return (percent / 100) * (max - min) + min;
    }

    calcValueByPos(position) {
      const pixelOffset = position - this.getSliderStart();
      const nextValue = this.trimAlignValue(this.calcValue(pixelOffset));
      return nextValue;
    }

    calcOffset(value) {
      const { min, max } = this.props;
      const customScale = this.getCustomScale();
      if (customScale) {
        return this.getOffsetForMark(value, customScale);
      }

      const ratio = (value - min) / (max - min);
      return ratio * 100;
    }

    getCustomScale() {
      const { marks, marksStyles } = this.props;
      if (!marksStyles || !Object.keys(marksStyles).length) {
        return null;
      }

      const marksKeys = Object.keys(marks).map(parseFloat).sort((a, b) => a - b);

      if (marksKeys.length < 2) {
        return null;
      }

      const sliderLength = this.getSliderLength();
      if (!sliderLength) {
        return null;
      }

      const cache = this._customScaleCache;
      if (cache && cache.marks === marks && cache.marksStyles === marksStyles && cache.sliderLength === sliderLength) {
        return cache.data;
      }

      // width of first mark should always be 0, remove if from calculation
      const marksSegments = marksKeys.slice(1, marksKeys.length);
      const marksWidths = marksSegments.map((_, i) => this.getMarkWidth(marksStyles[i + 1]));
      const totalFixedWidth = marksWidths.reduce((result, width) => result + (width || 0), 0);
      const flexibleMarksCount = marksWidths.reduce((result, width) => (!width ? result + 1 : result), 0);

      let widthScale = 1;
      if (totalFixedWidth > sliderLength) {
        widthScale = sliderLength / totalFixedWidth;
      }

      const remainingWidth = Math.max(sliderLength - totalFixedWidth * widthScale, 0);
      const flexibleMarkWidth = flexibleMarksCount > 0 ? remainingWidth / flexibleMarksCount : 0;

      const segments = [];
      let currentPx = 0;

      for (let index = 0; index < marksSegments.length; index += 1) {
        const startMark = marksKeys[index];
        const endMark = marksKeys[index + 1];
        const fixedWidth = marksWidths[index];
        const width = fixedWidth !== null ? fixedWidth * widthScale : flexibleMarkWidth;
        const startOffset = getPercent(currentPx, sliderLength);

        currentPx += width;

        const endOffset = getPercent(currentPx, sliderLength);

        segments.push({
          startMark,
          endMark,
          startOffset,
          endOffset,
          widthPercent: endOffset - startOffset,
        });
      }

      const data = {
        marksKeys,
        segments,
      };

      this._customScaleCache = {
        marks,
        marksStyles,
        sliderLength,
        data,
      };

      return data;
    }

    getMarkWidth(markStyle) {
      if (!markStyle || markStyle.width === undefined || markStyle.width === null) {
        return null;
      }

      if (isNumber(markStyle.width)) {
        return markStyle.width;
      }

      if (isString(markStyle.width)) {
        const numbers = markStyle.width.match(/\d+\.?\d*/g);

        if (!numbers) {
          return null;
        }

        const value = +numbers[0].trim();

        if (markStyle.width.includes('%')) {
          return getPercentFromValue(this.getSliderLength(), value);
        }

        if (markStyle.width.includes('px')) {
          return value;
        }
      }

      return null;
    }

    getOffsetForMark(mark, customScale) {
      const { marksKeys, segments } = customScale;

      if (mark <= marksKeys[0]) {
        return 0;
      }

      if (mark >= marksKeys[marksKeys.length - 1]) {
        return 100;
      }

      const segment = segments.find(({ startMark, endMark }) => mark >= startMark && mark <= endMark);
      if (!segment) {
        return 0;
      }

      const { startMark, endMark, startOffset, widthPercent } = segment;

      const ratio = (mark - startMark) / (endMark - startMark);
      return startOffset + ratio * widthPercent;
    }

    getValueByCustomOffset(offsetPercent, customScale) {
      const { marksKeys, segments } = customScale;
      if (offsetPercent <= 0) {
        return marksKeys[0];
      }
      if (offsetPercent >= 100) {
        return marksKeys[marksKeys.length - 1];
      }

      const segment = segments.find(
        ({ startOffset, endOffset }) =>
          offsetPercent >= startOffset && offsetPercent <= endOffset,
      );
      if (!segment) {
        return marksKeys[0];
      }

      const { startMark, endMark, startOffset, widthPercent } = segment;
      if (widthPercent === 0) {
        return startMark;
      }

      const ratio = (offsetPercent - startOffset) / widthPercent;
      return startMark + ratio * (endMark - startMark);
    }

    saveSlider = (slider) => {
      if (slider && slider !== this.sliderRef) {
        this.sliderRef = slider;
        if (this.props.marksStyles) {
          this.forceUpdate();
        }
        return;
      }

      this.sliderRef = slider;
    };

    saveHandle(index, handle) {
      this.handlesRefs[index] = handle;
    }

    render() {
      const {
        prefixCls, className, marks, dots, step, included, disabled, vertical, min, max, children, maximumTrackStyle, style, railStyle, dotStyle, activeDotStyle,
      } = this.props;
      const { tracks, handles } = super.render();

      const customScale = this.getCustomScale();
      const getPointOffset = customScale ? (mark) => this.getOffsetForMark(mark, customScale) : null;

      const sliderClassName = classNames(prefixCls, {
        [`${prefixCls}-with-marks`]: Object.keys(marks).length,
        [`${prefixCls}-disabled`]: disabled,
        [`${prefixCls}-vertical`]: vertical,
        [className]: className,
      });
      return (
        <div
          ref={this.saveSlider}
          className={sliderClassName}
          onTouchStart={disabled ? noop : this.onTouchStart}
          onMouseDown={disabled ? noop : this.onMouseDown}
          onMouseUp={disabled ? noop : this.onMouseUp}
          onKeyDown={disabled ? noop : this.onKeyDown}
          onFocus={disabled ? noop : this.onFocus}
          onBlur={disabled ? noop : this.onBlur}
          style={style}
        >
          <div
            className={`${prefixCls}-rail`}
            style={{
              ...maximumTrackStyle,
              ...railStyle,
            }}
          />
          {tracks}
          <Steps
            prefixCls={prefixCls}
            vertical={vertical}
            marks={marks}
            getPointOffset={getPointOffset}
            dots={dots}
            step={step}
            included={included}
            lowerBound={this.getLowerBound()}
            upperBound={this.getUpperBound()}
            max={max}
            min={min}
            dotStyle={dotStyle}
            activeDotStyle={activeDotStyle}
          />
          {handles}
          <Marks
            className={`${prefixCls}-mark`}
            onClickLabel={disabled ? noop : this.onClickMarkLabel}
            vertical={vertical}
            marks={marks}
            getPointOffset={getPointOffset}
            included={included}
            lowerBound={this.getLowerBound()}
            upperBound={this.getUpperBound()}
            max={max}
            min={min}
          />
          {children}
        </div>
      );
    }
  };
}
